import { useEffect, useMemo, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { useSortableTable, SortIndicator } from "@/hooks/useSortableTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from "@/components/ui/sheet";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input as SearchInput } from "@/components/ui/input";
import {
    PencilSimple,
    Plus,
    UserMinus,
    MagnifyingGlass,
    ShieldCheck,
    UsersThree,
    Archive,
    ArrowCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "Admin", consultant: "Empleado", pm: "PM" };
const ROLE_STYLES = {
    admin: "bg-blue-100 text-blue-800 border-blue-200",
    pm: "bg-purple-100 text-purple-800 border-purple-200",
    consultant: "bg-slate-100 text-slate-700 border-slate-200",
};

const empty = {
    email: "",
    name: "",
    role: "consultant",
    department: "Internal",
    active: true,
    weekly_hours: 40,
    annual_vacation_days: 22,
    group_id: "",
};

export default function AdminUsers() {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [roleFilter, setRoleFilter] = useState("all");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [confirm, setConfirm] = useState(null); // { user, newRole }
    const [groups, setGroups] = useState([]);
    const [groupsOpen, setGroupsOpen] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [showArchived, setShowArchived] = useState(false);

    const load = () => http.get("/users").then((r) => setItems(r.data));
    const loadGroups = () => http.get("/groups").then((r) => setGroups(r.data));
    useEffect(() => {
        load();
        loadGroups();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            if (form.user_id) {
                await http.put(`/users/${form.user_id}`, form);
                toast.success("Usuario actualizado");
            } else {
                await http.post("/users", form);
                toast.success("Usuario creado");
            }
            setOpen(false);
            setForm(empty);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const edit = (u) => {
        setForm({ ...empty, ...u });
        setOpen(true);
    };
    const newOne = () => {
        setForm(empty);
        setOpen(true);
    };
    const remove = async (u) => {
        if (u.user_id === me?.user_id) {
            toast.error("No puedes desactivar tu propio usuario");
            return;
        }
        if (!window.confirm(`¿Desactivar a ${u.name}?`)) return;
        try {
            await http.delete(`/users/${u.user_id}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const toggleArchive = async (u) => {
        if (u.user_id === me?.user_id) {
            toast.error("No puedes archivar tu propio usuario");
            return;
        }
        const willArchive = !u.archived;
        const msg = willArchive
            ? `¿Archivar a ${u.name}? Dejará de aparecer en asignaciones, matriz de vacaciones y filtros de informes. Los datos históricos se conservan.`
            : `¿Restaurar a ${u.name} como usuario activo?`;
        if (!window.confirm(msg)) return;
        try {
            await http.patch(`/users/${u.user_id}/archive`, { archived: willArchive });
            toast.success(willArchive ? "Usuario archivado" : "Usuario restaurado");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const doRoleChange = async (user, newRole) => {
        try {
            await http.patch(`/users/${user.user_id}/role`, { role: newRole });
            toast.success(`Rol actualizado a ${ROLE_LABELS[newRole]}`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally {
            setConfirm(null);
        }
    };

    const requestRoleChange = (user, newRole) => {
        if (user.role === newRole) return;
        // Demoting an admin → confirm
        if (user.role === "admin" && newRole !== "admin") {
            setConfirm({ user, newRole });
            return;
        }
        doRoleChange(user, newRole);
    };

    const filtered = useMemo(() => {
        return items.filter((u) => {
            if (!showArchived && u.archived) return false;
            if (roleFilter !== "all" && u.role !== roleFilter) return false;
            if (
                q &&
                !u.name.toLowerCase().includes(q.toLowerCase()) &&
                !u.email.toLowerCase().includes(q.toLowerCase())
            )
                return false;
            return true;
        });
    }, [items, q, roleFilter, showArchived]);

    const adminsCount = items.filter((u) => u.role === "admin" && u.active && !u.archived).length;
    const pmCount = items.filter((u) => u.role === "pm" && u.active && !u.archived).length;
    const archivedCount = items.filter((u) => u.archived).length;

    const groupById = useMemo(() => {
        const m = {};
        for (const g of groups) m[g.group_id] = g;
        return m;
    }, [groups]);

    const accessors = useMemo(
        () => ({
            name: (u) => u.name || "",
            email: (u) => u.email || "",
            role: (u) => u.role || "",
            weekly: (u) => Number(u.weekly_hours || 0),
            group: (u) => groupById[u.group_id]?.name || "",
            rate: (u) => Number(groupById[u.group_id]?.hourly_rate || 0),
            vac: (u) => Number(u.annual_vacation_days || 0),
        }),
        [groupById],
    );
    const { sorted, sortKey, sortDir, headerProps } = useSortableTable(
        filtered,
        "admin-users",
        accessors,
        { initialKey: "name", initialDir: "asc" },
    );

    return (
        <div data-testid="page-admin-users">
            <PageHeader
                eyebrow="Administración"
                title="Usuarios y roles"
                description="Gestiona empleados. Cambia el rol directamente desde la lista — los cambios se aplican al momento."
                actions={
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setGroupsOpen(true)}
                            className="rounded-sm border-slate-300"
                            data-testid="groups-manage-btn"
                        >
                            Grupos y tarifas
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setMergeOpen(true)}
                            className="rounded-sm border-slate-300"
                            data-testid="merge-users-btn"
                        >
                            Fusionar usuarios
                        </Button>
                        <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button
                                onClick={newOne}
                                className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                data-testid="user-new-btn"
                            >
                                <Plus size={14} className="mr-2" /> Nuevo usuario
                            </Button>
                        </SheetTrigger>
                        <SheetContent className="rounded-none sm:max-w-md">
                            <SheetHeader>
                                <SheetTitle className="font-display">
                                    {form.user_id ? "Editar usuario" : "Nuevo usuario"}
                                </SheetTitle>
                                <SheetDescription className="text-xs text-slate-500">
                                    Datos personales, jornada semanal y vacaciones anuales.
                                </SheetDescription>
                            </SheetHeader>
                            <form onSubmit={submit} className="mt-6 space-y-3">
                                <div>
                                    <Label className="text-xs">Email</Label>
                                    <Input
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({ ...form, email: e.target.value })
                                        }
                                        className="mt-1 rounded-sm border-slate-300"
                                        data-testid="user-email"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs">Nombre</Label>
                                    <Input
                                        required
                                        value={form.name}
                                        onChange={(e) =>
                                            setForm({ ...form, name: e.target.value })
                                        }
                                        className="mt-1 rounded-sm border-slate-300"
                                        data-testid="user-name"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Rol</Label>
                                        <Select
                                            value={form.role}
                                            onValueChange={(v) =>
                                                setForm({ ...form, role: v })
                                            }
                                        >
                                            <SelectTrigger
                                                className="mt-1 rounded-sm border-slate-300"
                                                data-testid="user-role"
                                            >
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="admin">Admin</SelectItem>
                                                <SelectItem value="pm">
                                                    Project Manager
                                                </SelectItem>
                                                <SelectItem value="consultant">
                                                    Empleado
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Departamento</Label>
                                        <Input
                                            value={form.department || ""}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    department: e.target.value,
                                                })
                                            }
                                            className="mt-1 rounded-sm border-slate-300"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <Label className="text-xs">Jornada (h/sem)</Label>
                                        <Input
                                            type="number"
                                            step="0.5"
                                            value={form.weekly_hours}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    weekly_hours: Number(e.target.value),
                                                })
                                            }
                                            className="mt-1 rounded-sm border-slate-300 font-mono"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Vacaciones (d/año)</Label>
                                        <Input
                                            type="number"
                                            value={form.annual_vacation_days}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    annual_vacation_days: Number(
                                                        e.target.value,
                                                    ),
                                                })
                                            }
                                            className="mt-1 rounded-sm border-slate-300 font-mono"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs">Grupo / Tarifa</Label>
                                    <Select
                                        value={form.group_id || "none"}
                                        onValueChange={(v) =>
                                            setForm({ ...form, group_id: v === "none" ? "" : v })
                                        }
                                    >
                                        <SelectTrigger
                                            className="mt-1 rounded-sm border-slate-300"
                                            data-testid="user-group"
                                        >
                                            <SelectValue placeholder="Sin grupo" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Sin grupo</SelectItem>
                                            {groups.map((g) => (
                                                <SelectItem key={g.group_id} value={g.group_id}>
                                                    {g.name} — {g.hourly_rate.toFixed(2)} €/h
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-[11px] text-slate-500 mt-1">
                                        La tarifa horaria se calcula a partir del grupo asignado.
                                    </p>
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                                    data-testid="user-save"
                                >
                                    {form.user_id ? "Guardar" : "Crear"}
                                </Button>
                            </form>
                        </SheetContent>
                    </Sheet>
                    </div>
                }
            />
            <div className="p-6 lg:p-10 space-y-5">
                {/* Summary chips */}
                <div className="grid grid-cols-3 gap-px bg-slate-200 border border-slate-200">
                    <div className="bg-white p-4 flex items-center gap-3">
                        <ShieldCheck size={22} className="text-blue-700" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Administradores
                            </div>
                            <div
                                className="font-mono text-2xl font-bold"
                                data-testid="admin-count"
                            >
                                {adminsCount}
                            </div>
                        </div>
                    </div>
                    <div className="bg-white p-4 flex items-center gap-3">
                        <UsersThree size={22} className="text-purple-700" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Project Managers
                            </div>
                            <div className="font-mono text-2xl font-bold">{pmCount}</div>
                        </div>
                    </div>
                    <div className="bg-white p-4 flex items-center gap-3">
                        <UsersThree size={22} className="text-slate-500" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                                Total activos
                            </div>
                            <div className="font-mono text-2xl font-bold">
                                {items.filter((u) => u.active && !u.archived).length}
                            </div>
                            {archivedCount > 0 && (
                                <div
                                    className="text-[10px] text-slate-400 font-mono mt-0.5"
                                    data-testid="archived-counter"
                                >
                                    +{archivedCount} archivado{archivedCount === 1 ? "" : "s"}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="border border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <MagnifyingGlass
                            size={14}
                            className="absolute left-2 top-2.5 text-slate-400"
                        />
                        <SearchInput
                            placeholder="Buscar por nombre o email…"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="rounded-sm border-slate-300 pl-7 h-8"
                            data-testid="users-search"
                        />
                    </div>
                    <Select value={roleFilter} onValueChange={setRoleFilter}>
                        <SelectTrigger
                            className="rounded-sm border-slate-300 w-40 h-8"
                            data-testid="users-role-filter"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los roles</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="pm">Project Manager</SelectItem>
                            <SelectItem value="consultant">Empleado</SelectItem>
                        </SelectContent>
                    </Select>
                    <label
                        className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none"
                        data-testid="toggle-show-archived-label"
                    >
                        <input
                            type="checkbox"
                            checked={showArchived}
                            onChange={(e) => setShowArchived(e.target.checked)}
                            className="rounded-sm"
                            data-testid="toggle-show-archived"
                        />
                        Mostrar archivados
                    </label>
                    <span className="text-xs text-slate-500 font-mono">
                        {filtered.length} de {items.length}
                    </span>
                </div>

                <div className="border border-slate-200 bg-white">
                    <table className="w-full text-sm" data-testid="users-table">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-semibold">
                                <th {...headerProps("name", "py-3 px-4 text-left")}>
                                    Empleado
                                    <SortIndicator active={sortKey === "name"} dir={sortDir} />
                                </th>
                                <th {...headerProps("email", "py-3 px-4 text-left")}>
                                    Email
                                    <SortIndicator active={sortKey === "email"} dir={sortDir} />
                                </th>
                                <th {...headerProps("role", "py-3 px-4 text-left w-48")}>
                                    Rol
                                    <SortIndicator active={sortKey === "role"} dir={sortDir} />
                                </th>
                                <th {...headerProps("weekly", "py-3 px-4 text-right")}>
                                    Jornada
                                    <SortIndicator active={sortKey === "weekly"} dir={sortDir} />
                                </th>
                                <th {...headerProps("group", "py-3 px-4 text-left w-40")}>
                                    Grupo · Tarifa
                                    <SortIndicator active={sortKey === "group"} dir={sortDir} />
                                </th>
                                <th {...headerProps("vac", "py-3 px-4 text-right")}>
                                    Vac.
                                    <SortIndicator active={sortKey === "vac"} dir={sortDir} />
                                </th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((u) => {
                                const isMe = u.user_id === me?.user_id;
                                return (
                                    <tr
                                        key={u.user_id}
                                        className={`border-b border-slate-100 hover:bg-slate-50 ${u.archived ? "opacity-60 bg-slate-50/60" : !u.active ? "opacity-50" : ""}`}
                                        data-testid={`user-row-${u.user_id}`}
                                    >
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-3">
                                                {u.picture ? (
                                                    <img
                                                        src={u.picture}
                                                        alt=""
                                                        className="w-8 h-8 object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-8 h-8 bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-600">
                                                        {u.name?.[0]?.toUpperCase()}
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-medium text-slate-900 flex items-center gap-2">
                                                        {u.name}
                                                        {isMe && (
                                                            <span className="text-[9px] uppercase tracking-wider bg-slate-950 text-white px-1.5 py-0.5">
                                                                Tú
                                                            </span>
                                                        )}
                                                        {u.archived && (
                                                            <span
                                                                className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 flex items-center gap-1"
                                                                data-testid={`user-archived-badge-${u.user_id}`}
                                                            >
                                                                <Archive size={9} />
                                                                Archivado
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-slate-500">
                                                        {u.department}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 font-mono text-slate-700 text-xs">
                                            {u.email}
                                        </td>
                                        <td className="py-3 px-4">
                                            <Select
                                                value={u.role}
                                                onValueChange={(v) =>
                                                    requestRoleChange(u, v)
                                                }
                                                disabled={isMe && u.role === "admin"}
                                            >
                                                <SelectTrigger
                                                    className={`rounded-sm h-7 text-xs font-semibold w-40 border ${ROLE_STYLES[u.role] || "border-slate-300"}`}
                                                    data-testid={`user-role-${u.user_id}`}
                                                >
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="admin">
                                                        Admin
                                                    </SelectItem>
                                                    <SelectItem value="pm">
                                                        Project Manager
                                                    </SelectItem>
                                                    <SelectItem value="consultant">
                                                        Empleado
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="py-3 px-4 text-right font-mono">
                                            {u.weekly_hours}h
                                        </td>
                                        <td className="py-3 px-4">
                                            {(() => {
                                                const g = groups.find((x) => x.group_id === u.group_id);
                                                if (!g) {
                                                    return (
                                                        <span
                                                            className="text-xs text-slate-400 italic"
                                                            data-testid={`user-group-cell-${u.user_id}`}
                                                        >
                                                            Sin grupo
                                                        </span>
                                                    );
                                                }
                                                return (
                                                    <div
                                                        className="text-xs"
                                                        data-testid={`user-group-cell-${u.user_id}`}
                                                    >
                                                        <div className="font-semibold text-slate-800">
                                                            {g.name}
                                                        </div>
                                                        <div className="font-mono text-slate-500">
                                                            {g.hourly_rate.toFixed(2)} €/h
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                        <td className="py-3 px-4 text-right font-mono">
                                            {u.annual_vacation_days}d
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button
                                                onClick={() => edit(u)}
                                                className="text-slate-500 hover:text-slate-900 mr-3"
                                                data-testid={`user-edit-${u.user_id}`}
                                            >
                                                <PencilSimple size={16} />
                                            </button>
                                            <button
                                                onClick={() => toggleArchive(u)}
                                                disabled={isMe}
                                                title={u.archived ? "Restaurar" : "Archivar (ex-empleado)"}
                                                className={`mr-3 disabled:opacity-30 disabled:cursor-not-allowed ${
                                                    u.archived
                                                        ? "text-emerald-600 hover:text-emerald-800"
                                                        : "text-slate-400 hover:text-amber-600"
                                                }`}
                                                data-testid={`user-archive-${u.user_id}`}
                                            >
                                                {u.archived ? (
                                                    <ArrowCounterClockwise size={16} />
                                                ) : (
                                                    <Archive size={16} />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => remove(u)}
                                                disabled={isMe}
                                                className="text-slate-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                                data-testid={`user-remove-${u.user_id}`}
                                            >
                                                <UserMinus size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
                <AlertDialogContent
                    className="rounded-sm"
                    data-testid="role-change-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            ¿Quitar permisos de administrador?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirm?.user?.name} pasará de{" "}
                            <span className="font-semibold">Admin</span> a{" "}
                            <span className="font-semibold">
                                {ROLE_LABELS[confirm?.newRole]}
                            </span>
                            . Perderá acceso a la gestión global.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm">
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            onClick={() =>
                                doRoleChange(confirm.user, confirm.newRole)
                            }
                            data-testid="role-change-confirm"
                        >
                            Confirmar
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <GroupsManagerSheet
                open={groupsOpen}
                onOpenChange={setGroupsOpen}
                groups={groups}
                reload={async () => {
                    await loadGroups();
                    load();
                }}
            />

            <MergeUsersSheet
                open={mergeOpen}
                onOpenChange={setMergeOpen}
                users={items}
                reload={load}
            />
        </div>
    );
}

function GroupsManagerSheet({ open, onOpenChange, groups, reload }) {
    const [form, setForm] = useState({ name: "", hourly_rate: "" });
    const [editing, setEditing] = useState(null);

    const reset = () => {
        setForm({ name: "", hourly_rate: "" });
        setEditing(null);
    };

    const save = async (e) => {
        e.preventDefault();
        const rate = Number(form.hourly_rate);
        if (!form.name.trim() || Number.isNaN(rate) || rate < 0) {
            toast.error("Nombre y tarifa válidos requeridos");
            return;
        }
        try {
            if (editing) {
                await http.put(`/groups/${editing.group_id}`, {
                    name: form.name.trim(),
                    hourly_rate: rate,
                });
                toast.success("Grupo actualizado");
            } else {
                await http.post("/groups", {
                    name: form.name.trim(),
                    hourly_rate: rate,
                });
                toast.success("Grupo creado");
            }
            reset();
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const remove = async (g) => {
        if (!window.confirm(`¿Eliminar el grupo "${g.name}"?`)) return;
        try {
            await http.delete(`/groups/${g.group_id}`);
            toast.success("Grupo eliminado");
            reset();
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        }
    };

    const startEdit = (g) => {
        setEditing(g);
        setForm({ name: g.name, hourly_rate: String(g.hourly_rate) });
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="rounded-none sm:max-w-md" data-testid="groups-sheet">
                <SheetHeader>
                    <SheetTitle className="font-display">Grupos y tarifas</SheetTitle>
                    <SheetDescription className="text-xs text-slate-500">
                        Define los grupos salariales. La tarifa horaria del informe se toma del
                        grupo asignado a cada persona.
                    </SheetDescription>
                </SheetHeader>
                <div className="mt-4 space-y-2 max-h-[45vh] overflow-y-auto border border-slate-200">
                    {groups.length === 0 && (
                        <div className="p-4 text-sm text-slate-500 text-center">
                            Aún no hay grupos.
                        </div>
                    )}
                    {groups.map((g) => (
                        <div
                            key={g.group_id}
                            className="flex items-center justify-between px-3 py-2 border-b border-slate-100 last:border-b-0"
                            data-testid={`group-row-${g.group_id}`}
                        >
                            <div>
                                <div className="text-sm font-semibold text-slate-900">
                                    {g.name}
                                </div>
                                <div className="text-xs font-mono text-slate-500">
                                    {g.hourly_rate.toFixed(2)} €/h
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => startEdit(g)}
                                    className="text-slate-500 hover:text-slate-900 p-1"
                                    data-testid={`group-edit-${g.group_id}`}
                                >
                                    <PencilSimple size={14} />
                                </button>
                                <button
                                    onClick={() => remove(g)}
                                    className="text-slate-400 hover:text-red-600 p-1"
                                    data-testid={`group-remove-${g.group_id}`}
                                >
                                    <UserMinus size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <form onSubmit={save} className="mt-5 space-y-3 border-t border-slate-200 pt-4">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                        {editing ? `Editar "${editing.name}"` : "Nuevo grupo"}
                    </div>
                    <div>
                        <Label className="text-xs">Nombre</Label>
                        <Input
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="mt-1 rounded-sm border-slate-300"
                            placeholder="Ej. Senior"
                            data-testid="group-name"
                        />
                    </div>
                    <div>
                        <Label className="text-xs">Tarifa (€/h)</Label>
                        <Input
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            value={form.hourly_rate}
                            onChange={(e) =>
                                setForm({ ...form, hourly_rate: e.target.value })
                            }
                            className="mt-1 rounded-sm border-slate-300 font-mono"
                            placeholder="29.00"
                            data-testid="group-rate"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="submit"
                            className="flex-1 rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="group-save"
                        >
                            {editing ? "Guardar cambios" : "Añadir grupo"}
                        </Button>
                        {editing && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={reset}
                                className="rounded-sm"
                            >
                                Cancelar
                            </Button>
                        )}
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
}


function MergeUsersSheet({ open, onOpenChange, users, reload }) {
    const [sourceEmail, setSourceEmail] = useState("");
    const [targetEmail, setTargetEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const reset = () => {
        setSourceEmail("");
        setTargetEmail("");
    };

    const targetExists = useMemo(
        () => users.some((u) => u.email?.toLowerCase() === targetEmail.trim().toLowerCase()),
        [users, targetEmail],
    );

    const run = async () => {
        const src = sourceEmail.trim().toLowerCase();
        const tgt = targetEmail.trim().toLowerCase();
        if (!src || !tgt) {
            toast.error("Indica ambos emails");
            return;
        }
        if (src === tgt) {
            toast.error("Los emails no pueden ser iguales");
            return;
        }
        setBusy(true);
        try {
            const r = await http.post("/users/merge", {
                source_email: src,
                target_email: tgt,
            });
            toast.success(r.data?.message || "Fusión completada");
            reset();
            setConfirmOpen(false);
            onOpenChange(false);
            await reload();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error en la fusión");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={onOpenChange}>
                <SheetContent className="rounded-none sm:max-w-md" data-testid="merge-sheet">
                    <SheetHeader>
                        <SheetTitle className="font-display">Fusionar usuarios</SheetTitle>
                        <SheetDescription className="text-xs text-slate-500">
                            Mueve todas las asignaciones, horas, gastos y ausencias del usuario
                            origen al usuario destino. Útil cuando los datos quedaron asociados a
                            un email incorrecto.
                        </SheetDescription>
                    </SheetHeader>
                    <div className="mt-6 space-y-4">
                        <div>
                            <Label className="text-xs">Email origen (con los datos)</Label>
                            <Input
                                type="email"
                                value={sourceEmail}
                                onChange={(e) => setSourceEmail(e.target.value)}
                                placeholder="manzano@ingartek.com"
                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                data-testid="merge-source-email"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Email destino (usuario correcto)</Label>
                            <Input
                                type="email"
                                value={targetEmail}
                                onChange={(e) => setTargetEmail(e.target.value)}
                                placeholder="manzanos@ingartek.com"
                                className="mt-1 rounded-sm border-slate-300 font-mono"
                                data-testid="merge-target-email"
                            />
                        </div>
                        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 p-3 leading-relaxed">
                            {targetEmail.trim() ? (
                                targetExists ? (
                                    <span>
                                        El destino <b>ya existe</b>: se moverán los datos del
                                        origen al destino y el usuario origen se eliminará.
                                    </span>
                                ) : (
                                    <span>
                                        El destino <b>no existe</b>: simplemente se cambiará el
                                        email del usuario origen a este nuevo email.
                                    </span>
                                )
                            ) : (
                                <span>Introduce el email destino para ver el efecto.</span>
                            )}
                        </div>
                        <Button
                            onClick={() => setConfirmOpen(true)}
                            disabled={busy || !sourceEmail.trim() || !targetEmail.trim()}
                            className="w-full rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            data-testid="merge-run-btn"
                        >
                            Fusionar
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={confirmOpen} onOpenChange={(v) => !busy && setConfirmOpen(v)}>
                <AlertDialogContent className="rounded-sm" data-testid="merge-confirm-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-display">
                            ¿Confirmar fusión?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Todos los datos de{" "}
                            <span className="font-mono font-semibold">
                                {sourceEmail.trim().toLowerCase()}
                            </span>{" "}
                            pasarán a{" "}
                            <span className="font-mono font-semibold">
                                {targetEmail.trim().toLowerCase()}
                            </span>
                            {targetExists
                                ? ". El usuario origen se eliminará. Esta acción no se puede deshacer."
                                : ". Se cambiará el email del usuario origen."}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-sm" disabled={busy}>
                            Cancelar
                        </AlertDialogCancel>
                        <AlertDialogAction
                            className="rounded-sm bg-[#002BAF] hover:bg-[#001F82]"
                            onClick={(e) => {
                                e.preventDefault();
                                run();
                            }}
                            data-testid="merge-confirm-btn"
                        >
                            {busy ? "Fusionando…" : "Sí, fusionar"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
